<!-- PAGE MODE (/cash-denomination, F12): page header, count card (Day In | Day Out) and register summary -->
<ng-container *ngIf="mode === 'page'">
  <app-page-header
    title="Cash Denomination"
    [subtitle]="(currentDate | date: 'EEE, MMM dd, yyyy') ?? ''"
    icon="pi pi-money-bill"
    (back)="goBackToHome()"
  ></app-page-header>

  <div class="cd-body">
    <!-- Left: denomination counts -->
    <article class="cd-card cd-card--count">
      <p-tabView (onChange)="onTabChange($event)" styleClass="modern-denom-tabs">
        <!-- DAY IN (OPENING CASH) TAB -->
        <p-tabPanel>
          <ng-template pTemplate="header">
            <div class="tab-pill-item">
              <span>Day In (Opening)</span>
              <span class="tab-total-badge" *ngIf="dayInTotal > 0">₹{{ dayInTotal | number: "1.0-0" }}</span>
            </div>
          </ng-template>

          <div class="tab-content-wrapper">
            <div class="table-top-bar">
              <span class="section-title">Opening Cash Denominations</span>
              <button
                type="button"
                class="premium-btn premium-btn-primary save-denom-btn"
                (click)="saveAllDayIn()"
                [disabled]="!hasDayInChanges || isDayInDisabled"
              >
                <i class="pi pi-check"></i>
                <span>{{ isDayInDisabled ? "Day In Saved" : "Save Day In" }}</span>
                <span *ngIf="hasDayInChanges && !isDayInDisabled" class="unsaved-dot" title="Unsaved changes"></span>
              </button>
            </div>

            <div class="denomination-table-container">
              <p-table [value]="fixedDenominations" styleClass="p-datatable-sm modern-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th class="col-amount">Denomination</th>
                    <th class="col-qty is-center">Count / Quantity</th>
                    <th class="col-total is-end">Total Amount</th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-denom let-ri="rowIndex">
                  <tr [class.has-value]="denom.dayInQuantity > 0">
                    <td class="col-amount">
                      <div class="denomination-display note-{{ denom.amount }}">
                        <span class="curr-sym">₹</span>
                        <span class="amount-num">{{ denom.amount }}</span>
                        <span class="amount-tag">{{ denom.amount >= 10 ? "Note" : "Coin" }}</span>
                      </div>
                    </td>
                    <td class="col-qty is-center">
                      <div class="qty-input-box">
                        <p-inputNumber
                          [inputId]="'denom_dayIn_' + ri"
                          [(ngModel)]="denom.dayInQuantity"
                          [min]="0"
                          [max]="99999"
                          [showButtons]="false"
                          [disabled]="isDayInDisabled"
                          (onInput)="calculateDenominationTotal(denom)"
                          (keydown.enter)="focusNextDenomination(ri, 'dayIn')"
                          placeholder="0"
                          styleClass="qty-input"
                        >
                        </p-inputNumber>
                      </div>
                    </td>
                    <td class="col-total is-end">
                      <div class="total-display" [class.has-total]="denom.dayInTotal > 0">
                        ₹{{ denom.dayInTotal | number: "1.0-0" }}
                      </div>
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="footer">
                  <tr class="table-summary-footer">
                    <td class="col-amount"><span class="footer-title">Total Opening Cash</span></td>
                    <td class="col-qty is-center">
                      <span class="footer-items-badge">{{ getTotalItemsCount("dayIn") }} items</span>
                    </td>
                    <td class="col-total is-end">
                      <span class="footer-grand-total">₹{{ dayInTotal | number: "1.0-0" }}</span>
                    </td>
                  </tr>
                </ng-template>
              </p-table>
            </div>
          </div>
        </p-tabPanel>

        <!-- DAY OUT (CLOSING CASH) TAB -->
        <p-tabPanel>
          <ng-template pTemplate="header">
            <div class="tab-pill-item">
              <span>Day Out (Closing)</span>
              <span class="tab-total-badge" *ngIf="dayCloseTotal > 0">₹{{ dayCloseTotal | number: "1.0-0" }}</span>
            </div>
          </ng-template>

          <div class="tab-content-wrapper">
            <div class="table-top-bar">
              <span class="section-title">
                Closing Cash Denominations
                <span class="section-count">10 Denominations</span>
              </span>
              <button
                type="button"
                class="premium-btn premium-btn-primary save-denom-btn"
                (click)="saveAllDayOut()"
                [disabled]="!hasDayOutChanges || isDayOutDisabled"
              >
                <i class="pi pi-check"></i>
                <span>{{ isDayOutDisabled ? "Day Out Saved" : "Save Day Out" }}</span>
                <span *ngIf="hasDayOutChanges && !isDayOutDisabled" class="unsaved-dot" title="Unsaved changes"></span>
              </button>
            </div>

            <div class="denomination-table-container">
              <p-table [value]="fixedDenominations" styleClass="p-datatable-sm modern-table">
                <ng-template pTemplate="header">
                  <tr>
                    <th class="col-amount-dayout">Denomination</th>
                    <th class="col-qty-dayout is-center">Count / Quantity</th>
                    <th class="col-total-dayout is-end">Total Amount</th>
                    <th class="col-verify is-center">
                      <div class="verify-header-box" title="Verify All Denominations">
                        <p-checkbox
                          [(ngModel)]="allVerified"
                          [binary]="true"
                          [disabled]="isDayOutDisabled"
                          pTooltip="Click to verify all denominations"
                          tooltipPosition="top"
                        >
                        </p-checkbox>
                        <span class="verify-text">Verify</span>
                      </div>
                    </th>
                  </tr>
                </ng-template>
                <ng-template pTemplate="body" let-denom let-ri="rowIndex">
                  <tr [class.has-value]="denom.dayOutQuantity > 0">
                    <td class="col-amount-dayout">
                      <div class="denomination-display note-{{ denom.amount }}">
                        <span class="curr-sym">₹</span>
                        <span class="amount-num">{{ denom.amount }}</span>
                        <span class="amount-tag">{{ denom.amount >= 10 ? "Note" : "Coin" }}</span>
                      </div>
                    </td>
                    <td class="col-qty-dayout is-center">
                      <div class="qty-input-box">
                        <p-inputNumber
                          [inputId]="'denom_dayOut_' + ri"
                          [(ngModel)]="denom.dayOutQuantity"
                          [min]="0"
                          [max]="99999"
                          [showButtons]="false"
                          [disabled]="isDayOutDisabled"
                          (onInput)="calculateDenominationTotal(denom)"
                          (keydown.enter)="focusNextDenomination(ri, 'dayOut')"
                          placeholder="0"
                          styleClass="qty-input"
                        >
                        </p-inputNumber>
                      </div>
                    </td>
                    <td class="col-total-dayout is-end">
                      <div class="total-display" [class.has-total]="denom.dayOutTotal > 0">
                        ₹{{ denom.dayOutTotal | number: "1.0-0" }}
                      </div>
                    </td>
                    <td class="col-verify is-center">
                      <p-checkbox
                        [(ngModel)]="denom.dayCloseVerify"
                        [binary]="true"
                        [disabled]="isDayOutDisabled"
                        pTooltip="Mark as verified"
                        tooltipPosition="left"
                      >
                      </p-checkbox>
                    </td>
                  </tr>
                </ng-template>
                <ng-template pTemplate="footer">
                  <tr class="table-summary-footer">
                    <td class="col-amount-dayout"><span class="footer-title">Total Closing Cash</span></td>
                    <td class="col-qty-dayout is-center">
                      <span class="footer-items-badge">{{ getTotalItemsCount("dayOut") }} items</span>
                    </td>
                    <td class="col-total-dayout is-end">
                      <span class="footer-grand-total">₹{{ dayCloseTotal | number: "1.0-0" }}</span>
                    </td>
                    <td class="col-verify"></td>
                  </tr>
                </ng-template>
              </p-table>
            </div>
          </div>
        </p-tabPanel>
      </p-tabView>
    </article>

    <!-- Right: register summary -->
    <aside class="cd-card cd-card--summary">
      <div class="cd-card__header">
        <h2 class="cd-card__title">Register Summary</h2>
        <span class="cd-card__sub">Daily cash &amp; payment collection</span>
      </div>

      <div class="summary-body-content">
        <div class="day-totals-stack">
          <div class="summary-kpi-card day-in-card">
            <div class="kpi-card-header">
              <span class="kpi-title">Day In Total</span>
              <span class="kpi-status-badge" [class.saved]="isDayInDisabled">
                {{ isDayInDisabled ? "Saved" : "Pending" }}
              </span>
            </div>
            <div class="kpi-card-row">
              <div class="kpi-value-text">₹{{ dayInTotal | number: "1.0-0" }}</div>
              <div class="kpi-footer-sub">{{ getTotalItemsCount("dayIn") }} items counted</div>
            </div>
          </div>

          <div class="summary-kpi-card day-out-card">
            <div class="kpi-card-header">
              <span class="kpi-title">Day Out Total</span>
              <span class="kpi-status-badge" [class.saved]="isDayOutDisabled">
                {{ isDayOutDisabled ? "Saved" : "Pending" }}
              </span>
            </div>
            <div class="kpi-card-row">
              <div class="kpi-value-text">₹{{ dayCloseTotal | number: "1.0-0" }}</div>
              <div class="kpi-footer-sub">{{ getTotalItemsCount("dayOut") }} items counted</div>
            </div>
          </div>
        </div>

        <div class="payment-summary-box">
          <div class="summary-heading">Payment Collection Summary</div>

          <div class="payment-summary-list">
            <div class="payment-line-item">
              <span class="line-icon cash-icon"><i class="pi pi-money-bill"></i></span>
              <span class="line-name">Cash Amount</span>
              <span class="kpi-mode-chip cash-chip">Cash</span>
              <span class="line-amount">₹{{ toInt(daySaleData.cashamount) | number: "1.0-0" }}</span>
            </div>
            <div class="payment-line-item">
              <span class="line-icon card-icon"><i class="pi pi-credit-card"></i></span>
              <span class="line-name">Card Amount</span>
              <span class="kpi-mode-chip card-chip">Card</span>
              <span class="line-amount">₹{{ toInt(daySaleData.cardamount) | number: "1.0-0" }}</span>
            </div>
            <div class="payment-line-item">
              <span class="line-icon upi-icon"><i class="pi pi-qrcode"></i></span>
              <span class="line-name">UPI Amount</span>
              <span class="kpi-mode-chip upi-chip">UPI</span>
              <span class="line-amount">₹{{ toInt(daySaleData.upiamount) | number: "1.0-0" }}</span>
            </div>
            <div class="payment-line-item">
              <span class="line-icon redeem-icon"><i class="pi pi-gift"></i></span>
              <span class="line-name">Redeem Amount</span>
              <span class="kpi-mode-chip redeem-chip">Voucher</span>
              <span class="line-amount">₹{{ toInt(daySaleData.redeemamount) | number: "1.0-0" }}</span>
            </div>
          </div>
        </div>

        <div class="total-sales-banner">
          <span class="banner-title">Total Amount</span>
          <span class="banner-amount">
            ₹{{
              (toInt(daySaleData.cashamount) + toInt(daySaleData.cardamount) + toInt(daySaleData.upiamount))
                | number: "1.0-0"
            }}
          </span>
        </div>
      </div>
    </aside>
  </div>
</ng-container>

<!-- DIALOG MODE: Compact layout for Cash In/Out dialogs -->
<!-- Sits inside the Opening / Closing Cash p-dialog shells (premium-dialog); it brings the body and footer -->
<div class="cash-denomination-dialog" *ngIf="mode === 'dialog'">
  <div class="premium-body">
  <!-- Day In Content -->
  <div class="dialog-content" *ngIf="initialTab === 'dayIn'">
    <div class="dialog-summary">
      <div class="summary-badge">
        <span class="label">Day In Total:</span>
        <span class="value">{{
          dayInTotal | currency: "INR" : "symbol" : "1.0-0"
        }}</span>
      </div>
    </div>

    <div class="denomination-table-container dialog-table">
      <p-table
        [value]="fixedDenominations"
        styleClass="p-datatable-sm modern-table dialog-denom-table"
        [scrollable]="true"
        scrollHeight="300px"
      >
        <ng-template pTemplate="header">
          <tr>
            <th class="col-amount-dialog">Amount</th>
            <th class="col-qty-dialog">Qty</th>
            <th class="col-total-dialog">Total</th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-denom>
          <tr>
            <td>
              <strong>{{
                denom.amount | currency: "INR" : "symbol" : "1.0-0"
              }}</strong>
            </td>
            <td>
              <p-inputNumber
                [(ngModel)]="denom.dayInQuantity"
                [min]="0"
                [showButtons]="false"
                [disabled]="isDayInDisabled"
                (onInput)="calculateDenominationTotal(denom)"
                styleClass="dialog-input"
              >
              </p-inputNumber>
            </td>
            <td>
              <strong>{{
                denom.dayInTotal | currency: "INR" : "symbol" : "1.0-0"
              }}</strong>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  <!-- Day Out Content -->
  <div class="dialog-content" *ngIf="initialTab === 'dayOut'">
    <div class="dialog-summary">
      <div class="summary-badge">
        <span class="label">Day Out Total:</span>
        <span class="value">{{
          dayCloseTotal | currency: "INR" : "symbol" : "1.0-0"
        }}</span>
      </div>
    </div>

    <div class="denomination-table-container dialog-table">
      <p-table
        [value]="fixedDenominations"
        styleClass="p-datatable-sm modern-table dialog-denom-table"
        [scrollable]="true"
        scrollHeight="300px"
      >
        <ng-template pTemplate="header">
          <tr>
            <th class="col-amount-dayout">Amount</th>
            <th class="col-qty-dayout">Qty</th>
            <th class="col-total-dayout">Total</th>
            <th class="col-verify is-center">
              <p-checkbox
                [(ngModel)]="allVerified"
                [binary]="true"
                [disabled]="isDayOutDisabled"
                pTooltip="Click to verify all denominations"
                tooltipPosition="top"
              >
              </p-checkbox>
            </th>
          </tr>
        </ng-template>
        <ng-template pTemplate="body" let-denom>
          <tr>
            <td>
              <strong>{{
                denom.amount | currency: "INR" : "symbol" : "1.0-0"
              }}</strong>
            </td>
            <td>
              <p-inputNumber
                [(ngModel)]="denom.dayOutQuantity"
                [min]="0"
                [showButtons]="false"
                [disabled]="isDayOutDisabled"
                (onInput)="calculateDenominationTotal(denom)"
                styleClass="dialog-input"
              >
              </p-inputNumber>
            </td>
            <td>
              <strong>{{
                denom.dayOutTotal | currency: "INR" : "symbol" : "1.0-0"
              }}</strong>
            </td>
            <td class="is-center">
              <p-checkbox
                [(ngModel)]="denom.dayCloseVerify"
                [binary]="true"
                [disabled]="isDayOutDisabled"
                pTooltip="Mark as verified"
                tooltipPosition="left"
              >
              </p-checkbox>
            </td>
          </tr>
        </ng-template>
      </p-table>
    </div>
  </div>

  </div>

  <!-- Skip (secondary) first, Save & Continue (the one primary) last -->
  <div class="premium-footer">
    <button *ngIf="canSkip" type="button" class="premium-btn premium-btn-secondary" (click)="skip()">
      <i class="pi pi-times"></i>
      Skip
    </button>
    <button
      type="button"
      class="premium-btn premium-btn-primary"
      [disabled]="(initialTab === 'dayIn' && isDayInDisabled) || (initialTab === 'dayOut' && isDayOutDisabled)"
      (click)="saveAndContinue()"
    >
      <i class="pi pi-check"></i>
      {{
        (initialTab === "dayIn" && isDayInDisabled) || (initialTab === "dayOut" && isDayOutDisabled)
          ? "Already Saved"
          : "Save & Continue"
      }}
    </button>
  </div>
</div>

<p-toast></p-toast>
<p-confirmDialog></p-confirmDialog>
